import React, { FC, useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import Button from 'components/Button';
import { useCreateAgencyMutation, useUpdateAgencyMutation } from 'core/api/agencies';
import { useModalStore } from 'stores/modal';
import Agency from 'core/models/agency';
import { SSpacedRow } from 'styles/layout';
import TextInput from 'components/Forms/lib/TextInput';
import { OptionsProps } from 'components/Forms/lib/SelectInput';
import { SelectInput } from 'components/Forms/lib';
import { Civility } from 'core/enums/civility';
import { enumKeys } from 'utils/common';
import { REGEXP } from 'constants/validationsForm';
import { DisabledField } from 'core/useCases/modal/types';
import useResponsive from 'hooks/responsive';

type UserProfile = {
    civility: Civility;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    mobileNumber: string;
    job: string;
};

type Inputs = {
    name: string;
    email: string;
    userProfile: UserProfile;
};

type AgencyFormProps = {
    closeModal(): void;
    defaultValues?: Partial<Agency>;
    disabledFields?: Array<DisabledField>;
};

const computeData = (agency?: Partial<Agency>): Partial<Inputs> | undefined => {
    if (!agency) {
        return undefined;
    }
     
    const { id, ...data } = agency;
    const computedData = {
        ...data,
    } as Partial<Inputs>;

    return computedData;
};

const AgencyForm: FC<AgencyFormProps> = ({ closeModal, defaultValues }) => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const closeModalStore = useModalStore((s) => s.closeModal);
    const createAgency = useCreateAgencyMutation();
    const updateAgency = useUpdateAgencyMutation();
    const [civilityOptions, setCivilityOptions] = useState<OptionsProps>([]);

    useEffect(() => {
        const civilityOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(Civility)) {
            civilityOptionsComputed.push({
                label: t(`common.civility.enum.${Civility[value]}`),
                value: Civility[value],
            });
        }
        setCivilityOptions(civilityOptionsComputed);
    }, [setCivilityOptions]);

    const methods = useForm<Inputs>({
        defaultValues: computeData(defaultValues),
    });
    const { handleSubmit, control } = methods;

    const onSubmit = handleSubmit(async (values: Inputs) => {
        try {
            if (defaultValues?.id) {
                // Update
                await updateAgency.mutateAsync({ id: defaultValues.id, inputs: values });
                closeModalStore();
            } else {
                // Creation
                await createAgency.mutateAsync(values);

                closeModalStore();
            }
            toast.success(t(`agency.${defaultValues?.id ? 'update' : 'creation'}.success`));
        } catch {
            toast.error(t(`agency.${defaultValues?.id ? 'update' : 'creation'}.error`));
            closeModalStore();
        }
    });

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <SSpacedRow>
                    <TextInput
                        label={t('common.agency.label')}
                        name="name"
                        placeholder={t('common.agency.placeholder')}
                        semi={!isMobile}
                        required
                    />
                </SSpacedRow>
                <TextInput
                    label={t('common.email.label')}
                    name="email"
                    pattern={{ message: t(REGEXP.EMAIL.message), value: REGEXP.EMAIL.value }}
                    placeholder={t('common.email.placeholder')}
                    semi={!isMobile}
                    type="email"
                    required
                />
                <SelectInput
                    control={control}
                    label={t('common.civility.label')}
                    name="userProfile.civility"
                    options={civilityOptions}
                    placeholder={t('common.civility.placeholder')}
                    semi={!isMobile}
                />
                <SSpacedRow>
                    <TextInput
                        label={t('common.first-name.label')}
                        name="userProfile.firstName"
                        placeholder={t('common.first-name.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.last-name.label')}
                        name="userProfile.lastName"
                        placeholder={t('common.last-name.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <SSpacedRow>
                    <TextInput
                        label={t('common.mobile-number.label')}
                        name="userProfile.mobileNumber"
                        pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                        placeholder={t('common.mobile-number.placeholder')}
                        semi={!isMobile}
                    />
                    <TextInput
                        label={t('common.phone-number.label')}
                        name="userProfile.phoneNumber"
                        pattern={{ message: t(REGEXP.PHONE_NUMBER.message), value: REGEXP.PHONE_NUMBER.value }}
                        placeholder={t('common.phone-number.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <SSpacedRow>
                    <TextInput
                        label={t('common.job.label')}
                        name="userProfile.job"
                        placeholder={t('common.job.placeholder')}
                        semi={!isMobile}
                    />
                </SSpacedRow>
                <BaseModal.BottomActions>
                    <Button onClick={() => closeModal()} variant="text">
                        {t('common.cancel')}
                    </Button>
                    <Button submit>{defaultValues?.id ? t('common.update') : t('common.create')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default AgencyForm;
